'use client';

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  Form,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Grid,
  InputNumber,
  AutoComplete,
  Drawer,
  Tabs,
  message,
} from "antd";
import { PlusOutlined, ShoppingCartOutlined, SearchOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { FormInstance } from "antd/es/form";
import { getItemExtensions } from "@/services/OrderAssemblyService";
import type { OrderItemUI } from "./OrderAssemblyPanel";
import type { SelectedExtension } from "./order-types";
import WarehouseFields, { useWarehouseCatalog } from "./WarehouseFields";

const { Text } = Typography;
const { useBreakpoint } = Grid;

export interface ModernItemsSectionProps {
  form: FormInstance;
  onAdd: () => void;
  items: OrderItemUI[];
  columns: ColumnsType<OrderItemUI>;
  total: number;
  priceLists?: { value: string; label: string }[];
  measureUnits: { value: string; label: string }[];
  disabled?: boolean; // when true, disable adding/searching items until order type selected
  onRemove?: (key: string) => void;
  /** Corregir una línea ya agregada (cantidad, precio, bodega, notas). */
  onEdit?: (item: OrderItemUI) => void;
  loadingAdd: boolean;
  stacked?: boolean;
  isActionDone?: boolean;
  orderReady?:boolean;
  referenceInputRef?: React.MutableRefObject<any>;
  highlightedRows?: Set<string>;
  /**
   * When true the component uses tabbed layout instead of the two column / collapse view.
   * Only applied on desktop; mobile still uses the drawer for the cart.
   */
  tabsLayout?: boolean;
  selectedOrderType?: string | null;
}


const DEFAULT_MEASURE_UNITS = [
  { value: "UND", label: "Unidad" },
  { value: "ML", label: "Metro Lineal" },
  { value: "KG", label: "Kilogramo" },
];


const ExtensionMultiSelect: React.FC<{ form: FormInstance }> = ({ form }) => {
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const useWatch: any = (Form as any).useWatch;
  const watchedReference: string | undefined = useWatch("reference", form);

  // Internal options: normalize them into a consistent structure { value, label, price, raw }
  const [internalOptions, setInternalOptions] = useState<any[]>([]);
  const [selectedExtensions, setSelectedExtensions] = useState<SelectedExtension[]>([]);
  const [filter, setFilter] = useState('');

  // Fetch extensions function
  const fetchExtensions = useCallback(async (reference: string, isRefetch: boolean = false, currentFilter: string = '') => {
    if (!reference) return;
    try {
      console.debug(`[ExtensionMultiSelect] ${isRefetch ? 'refetching' : 'fetching'} extensions for:`, reference, currentFilter ? `with filter: ${currentFilter}` : '');
      const exts = await getItemExtensions(reference, isRefetch, currentFilter);
      if (exts && Array.isArray(exts)) {
        // Al cargar o refetch, siempre nos aseguramos de tener la lista completa y única
        setInternalOptions(prev => {
          const combined = Array.from(new Set([...prev, ...exts]));
          // Ordenar para consistencia visual
          return combined.sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }));
        });
        
        // Actualizar extOptions en el formulario para persistencia
        const currentExts = form.getFieldValue('extOptions') || [];
        form.setFieldsValue({ 
          extOptions: Array.from(new Set([...currentExts, ...exts])).sort((a, b) => 
            String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
          )
        });
      }
    } catch (e) {
      console.error('[ExtensionMultiSelect] Error fetching extensions:', e);
    }
  }, [form]);

  // Initial load when reference changes
  useEffect(() => {
    if (watchedReference) {
      // Limpiar antes de cargar la nueva referencia
      setInternalOptions([]);
      setFilter('');
      fetchExtensions(watchedReference, false, '');
    } else {
      setInternalOptions([]);
      setSelectedExtensions([]);
      setFilter('');
      // Use setFieldsValue with a short delay or ensure it's not fighting applySiesaItemToForm
      form.setFieldsValue({ extOptions: [], selectedExtensions: "[]" });
    }
  }, [watchedReference, fetchExtensions, form]);

  // Sync with form on mount and when form changes (to handle external updates)
  useEffect(() => {
    const syncWithForm = () => {
      const formValue = form.getFieldValue("selectedExtensions");
      const parsed = formValue ? (typeof formValue === 'string' ? JSON.parse(formValue) : formValue) : [];
      
      // Update local state if different
      setSelectedExtensions(prev => {
        if (JSON.stringify(prev) !== JSON.stringify(parsed)) return parsed;
        return prev;
      });

      const formExts = form.getFieldValue("extOptions");
      if (formExts && Array.isArray(formExts) && formExts.length > 0) {
        setInternalOptions(prev => {
          if (JSON.stringify(prev) !== JSON.stringify(formExts)) return formExts;
          return prev;
        });
      }
    };

    syncWithForm();
    const timer = setTimeout(syncWithForm, 150);
    return () => clearTimeout(timer);
  }, [form, watchedReference]); // Depend on watchedReference to force re-sync on change

  // Listen for external events (e.g. from SearchSiesaRow)
  useEffect(() => {
    const handler = (ev: any) => {
      const d = ev?.detail;
      if (!d || !d.reference) return;
      
      // If reference matches, update options
      if (String(d.reference).trim().toUpperCase() === String(watchedReference || '').trim().toUpperCase()) {
        const exts = Array.isArray(d.extensions) ? d.extensions : [];
        if (exts.length > 0) {
          form.setFieldsValue({ extOptions: exts });
          setInternalOptions(exts);
        }
      }
    };
    window.addEventListener("siesa:extensions", handler);
    return () => window.removeEventListener("siesa:extensions", handler);
  }, [watchedReference, form]);

  // local refetch when filter is specific enough (e.g. searching for a missing extension)
  /* eslint-disable @typescript-eslint/no-use-before-define */
  useEffect(() => {
    // Si no hay referencia, no hacemos nada
    if (!watchedReference) return;
    
    // Si el filtro es corto, no hacemos refetch
    if (filter.length < 3) return;
    
    const timer = setTimeout(() => {
      // Solo hacer refetch si el filtro actual no coincide con nada de lo que ya tenemos
      const hasMatch = internalOptions.some(opt => {
        const normalized = normalizeOption(opt);
        return String(normalized.label).toLowerCase().includes(filter.toLowerCase()) ||
               String(normalized.value).toLowerCase().includes(filter.toLowerCase());
      });

      if (!hasMatch) {
        console.debug('[ExtensionMultiSelect] No matches found locally, forcing refetch for:', filter);
        fetchExtensions(watchedReference, true, filter); // Pasar el filtro al servicio
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [filter, watchedReference, fetchExtensions, internalOptions]);
  /* eslint-enable @typescript-eslint/no-use-before-define */

  function normalizeOption(opt: any) {
    if (opt === undefined || opt === null) return { value: '', label: '', price: 0 }
    if (typeof opt === 'string' || typeof opt === 'number') return { value: opt, label: String(opt), price: 0, raw: opt }
    const val = opt.EXT ?? opt.ext ?? opt.extension ?? opt.value ?? opt.id ?? opt.name ?? opt.code ?? undefined
    const label = opt.label ?? opt.name ?? opt.extension ?? opt.EXT ?? opt.value ?? (val !== undefined ? String(val) : JSON.stringify(opt))
    const price = opt.price ?? opt.unit_price ?? opt.PRICE ?? 0
    return { value: val, label, price, raw: opt }
  }

  const handleCheckboxChange = (extension: string, checked: boolean) => {
    let newSelected = [...selectedExtensions];
    if (checked) {
      newSelected.push({ extension, quantity: undefined });
    } else {
      newSelected = newSelected.filter(sel => sel.extension !== extension);
    }
    setSelectedExtensions(newSelected);
    form.setFieldsValue({ selectedExtensions: JSON.stringify(newSelected) });
  };

  const handleQuantityChange = (extension: string, quantity: number | null) => {
    let newSelected = selectedExtensions.map(sel =>
      sel.extension === extension ? { ...sel, quantity: quantity === null ? undefined : quantity } : sel
    );

    if (quantity !== null && quantity <= 0) {
      message.warning('La cantidad debe ser mayor a 0');
      newSelected = newSelected.map(sel =>
        sel.extension === extension ? { ...sel, quantity: undefined } : sel
      );
    }

    setSelectedExtensions(newSelected);
    form.setFieldsValue({ selectedExtensions: JSON.stringify(newSelected) });
  };

  const mappedOptions = (internalOptions || []).map((o: any) => normalizeOption(o));

  const filteredOptions = mappedOptions.filter((opt: any) => {
    if (!filter) return true;
    return String(opt.label).toLowerCase().includes(String(filter).toLowerCase()) ||
           String(opt.value).toLowerCase().includes(String(filter).toLowerCase());
  });

  return (
    <div style={{ width: '100%' }}>
      <Form.Item name="selectedExtensions" noStyle>
        <Input type="hidden" value={JSON.stringify(selectedExtensions)} />
      </Form.Item>
      <div style={{ marginBottom: 12, fontWeight: 600, color: '#262626', fontSize: 14 }}>Buscar y Seleccionar Extensiones:</div>
      <div style={{ marginBottom: 12 }}>
        <Input
          placeholder="Escribe para filtrar extensiones..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ width: '100%', borderRadius: 6 }}
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
        />
      </div>
      <br />
      <div style={{ maxHeight: 450, overflowY: 'auto', overflowX: 'hidden', border: '1px solid #f0f0f0', borderRadius: 8, padding: 10, background: '#fafafa' }}>
        {filteredOptions.length > 0 ? (
          filteredOptions.map((opt: any, idx: number) => {
            const isSelected = selectedExtensions.some((sel: SelectedExtension) => sel.extension === opt.value);
            const currentQuantity = selectedExtensions.find((sel: SelectedExtension) => sel.extension === opt.value)?.quantity;
            return (
              <div key={`${String(opt.value)}::${idx}`} style={{ marginBottom: 8, border: isSelected ? '2px solid var(--oa-accent)' : '1px solid #f0f0f0', borderRadius: 6, padding: 12, background: isSelected ? 'var(--oa-accent-bg)' : '#fff', transition: 'all 0.2s', boxShadow: isSelected ? '0 2px 8px rgba(0,0,0,0.12)' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={isSelected}
                    onChange={(e) => handleCheckboxChange(opt.value, e.target.checked)}
                    style={{ marginRight: 12 }}
                  />
                  <span style={{ flex: 1, fontWeight: isSelected ? 500 : 400 }}>{opt.label}</span>
                  {!isMobile && isSelected && (
                    <>
                      <span style={{ marginLeft: 8 }}>Cant:</span>
                      <InputNumber
                        min={1}
                        value={currentQuantity}
                        onChange={(value) => handleQuantityChange(opt.value, value)}
                        style={{ width: 80, marginLeft: 4 }}
                        placeholder="Cant."
                      />
                    </>
                  )}
                </div>
                {isMobile && isSelected && (
                  <div style={{ display: 'flex', alignItems: 'center', marginTop: 8, marginLeft: 24 }}>
                    <span style={{ marginRight: 8 }}>Cant:</span>
                    <InputNumber
                      min={1}
                      value={currentQuantity}
                      onChange={(value) => handleQuantityChange(opt.value, value)}
                      style={{ width: 80 }}
                      placeholder="Cant."
                    />
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div style={{ color: '#8c8c8c', textAlign: 'center', padding: 24, fontSize: 14 }}>
            {filter ? 'No se encontraron extensiones que coincidan con tu búsqueda' : 'No hay extensiones disponibles para este producto'}
          </div>
        )}
      </div>
    </div>
  );
};

export const ModernItemsSection: React.FC<ModernItemsSectionProps> = ({
  form,
  onAdd,
  items,
  columns,
  total,
  measureUnits,
  loadingAdd,
  stacked,
  onRemove,
  onEdit,
  isActionDone,
  disabled,
  highlightedRows,
  tabsLayout = false,
  selectedOrderType,
}) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // El maestro de bodegas se pide una sola vez y lo comparten los tres
  // layouts (apilado, dos columnas y pestañas).
  const warehouseCatalog = useWarehouseCatalog();

  const useWatch: any = (Form as any).useWatch;

  // Watches
  const watchedAvailablePrices: any[] = useWatch("availablePrices", form) || [];

  const watchedPriceList: string | undefined = useWatch("price_list", form);

  const watchedUnitPrice = useWatch("unit_price", form);

  const watchedMeasureUnit: string | undefined = useWatch("measure_unit", form);

  const [priceBounds, setPriceBounds] = useState<{ min?: number; max?: number }>(
    form.getFieldValue("_priceBounds") || {}
  );
  const [cartOpen, setCartOpen] = useState(false);

  // Respaldo: evento para precios
  const [eventPrices, setEventPrices] = useState<any[] | null>(null);
  useEffect(() => {
    const handler = (e: any) => setEventPrices(Array.isArray(e?.detail?.prices) ? e.detail.prices : null);
    window.addEventListener("siesa:availablePrices", handler);
    return () => window.removeEventListener("siesa:availablePrices", handler);
  }, []);

  const availablePrices = useMemo(
    () => (eventPrices && eventPrices.length ? eventPrices : watchedAvailablePrices || []),
    [eventPrices, watchedAvailablePrices]
  );

  const lists: string[] = useMemo(
    () => Array.from(new Set(availablePrices.map((p: any) => p.price_list))).filter(Boolean),
    [availablePrices]
  );

  // Ensure unit_price defaults to the minimum available price when a price_list is selected
  useEffect(() => {
    try {
      const curList = form.getFieldValue('price_list') || watchedPriceList
      if (!curList) return
      const fresh: any[] = form.getFieldValue('availablePrices') || availablePrices || []
      const found = (fresh || []).find((p: any) => String(p.price_list) === String(curList))
      if (found) {
        const minP = Number(found.min_price)
        const maxP = Number(found.max_price)
        setPriceBounds({ min: minP, max: maxP })
        // Always set unit_price to the minimum when the price list or available prices change
        form.setFieldsValue({ _priceBounds: { min: minP, max: maxP }, unit_price: minP, price_list: curList })
      }
    } catch (e) {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedPriceList, availablePrices, eventPrices])

  const priceOk = Boolean(
    (form.getFieldValue("price_list") || watchedPriceList) &&
      typeof (form.getFieldValue("_priceBounds")?.min ?? priceBounds.min) === "number"
  );

  const disabledAll = !!isActionDone || !!disabled;

  const cardStyle = { borderRadius: 10, boxShadow: "0 2px 6px rgba(0,0,0,0.04)", background: "var(--oa-accent-bg)" };
  const compactGutter = isMobile ? 8 : 12;

  // helpers used by both tabbed and row/stacked layouts
  const renderFormCard = () => (
    <Card 
      title={
        <Space>
          <PlusOutlined style={{ color: 'var(--oa-accent)' }} />
          <span style={{ fontWeight: 600 }}>Nuevo Artículo</span>
        </Space>
      } 
      size="small" 
      styles={{ body: { paddingTop: 12 } }} 
      style={cardStyle}
    >
      <Form
        form={form}
        layout="vertical"
        className="oa-compact"
        requiredMark={false}
        validateTrigger={["onChange", "onBlur"]}
        initialValues={{}}
        disabled={disabledAll}
      >
        <Row gutter={12} align="middle">
          <Col xs={24} md={24}>
            <Form.Item name="reference" noStyle>
              <Input type="hidden" value={form.getFieldValue("reference") ?? ""} readOnly />
            </Form.Item>
          </Col>

          <Col xs={24} md={24}>
            <ExtensionMultiSelect form={form} />
            <br /><br />
          </Col>
          

          <Col xs={12} md={8}>
            <Form.Item name="measure_unit" label="Unidad de Medida" rules={[{ required: true, message: "Seleccione unidad de medida" }]}>
              <AutoComplete
                options={(measureUnits?.length ? measureUnits : DEFAULT_MEASURE_UNITS).map((m) => ({ value: m.value, label: m.label }))}
                value={watchedMeasureUnit ?? undefined}
                onChange={(v) => form.setFieldsValue({ measure_unit: v })}
                placeholder="Ej: Unidad, Kg, Metro"
                disabled={disabledAll}
                style={{ width: "100%" }}
                filterOption
              />
            </Form.Item>
          </Col>

          <Col xs={12} md={8}>
            <Form.Item name="price_list" label="Lista de Precios" rules={[{ required: true, message: "Seleccione lista de precios" }]}>
              <Select
                value={form.getFieldValue("price_list") || undefined}
                showSearch
                placeholder={lists.length ? "Selecciona lista" : "Sin listas disponibles"}
                disabled={(lists ?? []).length === 0 || disabledAll}
                onChange={(val) => {
                  const valStr = val as string;
                  form.setFieldsValue({ price_list: valStr });
                  const fresh: any[] = form.getFieldValue("availablePrices") || availablePrices || [];
                  const found = (fresh || []).find((p: any) => String(p.price_list) === String(valStr));
                  if (found) {
                    const minP = Number(found.min_price);
                    const maxP = Number(found.max_price);
                    setPriceBounds({ min: minP, max: maxP });
                    const curUnit = form.getFieldValue('unit_price')
                    let newUnit = curUnit
                    if (curUnit === undefined || curUnit === null || curUnit === '') {
                      newUnit = minP
                    } else {
                      const num = Number(curUnit)
                      if (Number.isNaN(num) || num < minP) newUnit = minP
                      else if (num > maxP) newUnit = maxP
                    }
                    form.setFieldsValue({ _priceBounds: { min: minP, max: maxP }, unit_price: newUnit, price_list: valStr });
                  } else {
                    setPriceBounds({});
                    form.setFieldsValue({ _priceBounds: undefined, unit_price: undefined, price_list: "" });
                  }
                }}
                getPopupContainer={() => document.body}
                dropdownStyle={{ zIndex: 2147483647 }}
              >
                {(lists ?? []).map((code) => (
                  <Select.Option key={code} value={code}>{`Lista ${code}`}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <WarehouseFields form={form} disabled={disabledAll} catalog={warehouseCatalog} />
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name="unit_price"
              label="Precio Unitario"
              rules={[
                { required: true, message: "Ingrese precio unitario" },
                () => ({
                  validator: (_, value) => {
                    const bounds = form.getFieldValue("_priceBounds") || {};
                    const min = typeof bounds.min === "number" ? bounds.min : priceBounds.min;
                    const max = typeof bounds.max === "number" ? bounds.max : priceBounds.max;
                    const hasPriceList = Boolean(form.getFieldValue("price_list") || watchedPriceList);

                    if (!hasPriceList) {
                      if (value === undefined || value === null || value === "") return Promise.resolve();
                      const vnum = Number(value);
                      if (!Number.isNaN(vnum) && vnum === 0) return Promise.resolve();
                      return Promise.reject(new Error("El precio debe estar vacío o ser 0 si no hay lista de precios"));
                    }
                    const num = Number(value);
                    if (Number.isNaN(num)) return Promise.reject(new Error("Precio inválido"));
                    if (typeof min === "number" && num < min) return Promise.reject(new Error(`El precio debe ser >= ${min}`));
                    if (typeof max === "number" && num > max) return Promise.reject(new Error(`El precio debe ser <= ${max}`));
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber
                disabled={!priceOk || disabledAll}
                min={typeof priceBounds.min === "number" ? priceBounds.min : undefined}
                max={typeof priceBounds.max === "number" ? priceBounds.max : undefined}
                step={0.01}
                value={watchedUnitPrice ?? undefined}
                onChange={(v) => form.setFieldsValue({ unit_price: v })}
                style={{ width: "100%" }}
                placeholder="Ingresa precio unitario"
                stringMode
              />
              <div style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center" }}>
                {typeof priceBounds.min === "number" ? (
                  <Tag color="success" style={{ cursor: "pointer" }} onClick={() => form.setFieldsValue({ unit_price: priceBounds.min })}>
                    Min: ${Number(priceBounds.min).toLocaleString()}
                  </Tag>
                ) : (
                  <Tag color="default">Min: —</Tag>
                )}
                {typeof priceBounds.max === "number" ? (
                  <Tag color="warning">Max: ${Number(priceBounds.max).toLocaleString()}</Tag>
                ) : (
                  <Tag color="default">Max: —</Tag>
                )}
              </div>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name="notes" label="Notas Adicionales">
              <Input.TextArea 
                placeholder="Agregue observaciones o detalles específicos (máx. 140 caracteres)" 
                maxLength={140} 
                autoSize={{ minRows: 1, maxRows: 3 }} 
                value={form.getFieldValue("notes") ?? ""} 
                onChange={(e) => form.setFieldsValue({ notes: e.target.value })} 
                disabled={disabledAll} 
              />
            </Form.Item>
          </Col>
        </Row>

        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={onAdd} 
          loading={loadingAdd} 
          style={{ 
            width: "100%", 
            marginTop: 10, 
            height: 40,
            borderRadius: 8,
            fontWeight: 600,
            boxShadow: '0 2px 8px rgba(0,0,0,0.18)'
          }} 
          disabled={!priceOk || disabledAll}
        >
          Agregar Artículo
        </Button>
      </Form>
    </Card>
  );

  const renderCartContent = () => (
     <><div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between", width: "100%" }}>
      <Space>
        <ShoppingCartOutlined style={{ color: "var(--oa-accent)" }} />
        <span>Carrito</span>
      </Space>
    
    </div><Card size="small" styles={{ body: { padding: 8 } }} style={{ borderRadius: 8 }}>
        <Table
          tableLayout="fixed"
          columns={isMobile
            ? [
              { title: 'Producto', dataIndex: 'reference', key: 'reference', ellipsis: true },
              { title: 'Ext', dataIndex: 'extension', key: 'extension', width: 50 },
              { title: 'Cant', dataIndex: 'quantity', key: 'quantity', width: 60, align: 'right' as const },
              {
                title: 'Total',
                dataIndex: 'total',
                key: 'total',
                width: 80,
                align: 'right' as const,
                render: (v: number) => `$${v?.toLocaleString() || 0}`
              },
            ]
            : columns}
          dataSource={items}
          pagination={{ pageSize: 5 }}
          size="small"
          locale={{ emptyText: "Sin ítems todavía" }}
          rowKey="key"
          scroll={{ x: isMobile ? 560 : 1020 }}
          rowClassName={(record) => {
            const isHighlighted = highlightedRows?.has(record.key);
            return isHighlighted ? 'highlighted-row' : '';
          } }
          style={isMobile ? { fontSize: '12px' } : undefined}
          footer={() => <div style={{ textAlign: "right", fontWeight: 600 }}>Total: ${total === null || total === undefined ? "—" : total.toLocaleString()}</div>} />
      </Card></>
  );

  // if the component is asked to render tabs and we're on desktop, show a two-tab interface
  if (tabsLayout && !isMobile) {
    return (
      <div className={`oa-custom-tabs order-assembly-theme-${selectedOrderType || 'CPT'}`}>
        <style dangerouslySetInnerHTML={{ __html: `
          .oa-custom-tabs .ant-tabs-nav::before {
            border-bottom: 1px solid rgba(var(--oa-accent-rgb), 0.15);
          }
          .oa-custom-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
            color: var(--oa-accent) !important;
          }
          .oa-custom-tabs .ant-tabs-ink-bar {
            background: var(--oa-accent) !important;
          }
          .oa-custom-tabs .ant-tabs-tab:hover {
            color: var(--oa-accent) !important;
          }
          /* Estilo para las tabs superiores del Segmented en el padre */
          .oa-step-segment .ant-segmented-item-selected {
            background: var(--oa-accent) !important;
          }
        `}} />
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane key="1" tab="Agregar artículo">
            {renderFormCard()}
          </Tabs.TabPane>
          <Tabs.TabPane key="2" tab={<span><ShoppingCartOutlined style={{ marginRight: 4 }} />Carrito</span>}>
            {renderCartContent()}
          </Tabs.TabPane>
        </Tabs>
      </div>
    );
  }

  if (stacked || isMobile) {
    return (
      <div className="oa-items-stack" style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: '100%', overflowX: 'hidden', minHeight: "600px" }}>
        <div className="oa-items-stack-body">
          <Card size="small" styles={{ body: { padding: 12 } }} style={{ ...cardStyle, borderRadius: 12 }}>
          <div style={{ marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
            <Space>
              <PlusOutlined style={{ color: 'var(--oa-accent)', fontSize: 16 }} />
              <Text strong style={{ fontSize: 16 }}>Nuevo Artículo</Text>
            </Space>
          </div>
          <Form
            form={form}
            layout="vertical"
            className="oa-compact"
            requiredMark={false}
            validateTrigger={["onChange", "onBlur"]}
            initialValues={{ selectedExtensions: [] }}
            disabled={disabledAll}
          >
            <Row gutter={compactGutter}>
              <Col span={24} className="oa-ref-col">
                <Form.Item name="reference" noStyle>
                  <Input type="hidden" value={form.getFieldValue("reference") ?? ""} readOnly />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} span={24}>
                <ExtensionMultiSelect form={form} />
              </Col>

              <Col xs={12} sm={12} span={12}>
                <Form.Item name="measure_unit" label="Unidad de Medida" rules={[{ required: true, message: "Unidad de medida requerida" }]}>
                  <AutoComplete
                    options={(measureUnits?.length ? measureUnits : DEFAULT_MEASURE_UNITS).map((m) => ({ value: m.value, label: m.label }))}
                    value={watchedMeasureUnit ?? undefined}
                    onChange={(v) => form.setFieldsValue({ measure_unit: v })}
                    placeholder="Selecciona UM"
                    disabled={disabledAll}
                    style={{ width: "100%" }}
                    filterOption
                  />
                </Form.Item>
              </Col>

              <Col xs={12} sm={12} span={12}>
                <Form.Item name="price_list" label="Lista de Precios" rules={[{ required: true, message: "Seleccione una lista de precios" }]}>
                  <Select
                    value={form.getFieldValue("price_list") || undefined}
                    showSearch
                    placeholder={lists.length ? "Selecciona lista de precios" : "Sin listas disponibles"}
                    disabled={lists.length === 0 || disabledAll}
                    onChange={(val) => {
                      const valStr = val as string
                      form.setFieldsValue({ price_list: valStr });
                      const fresh: any[] = form.getFieldValue("availablePrices") || availablePrices || [];
                      const found = (fresh || []).find((p: any) => String(p.price_list) === String(valStr));
                      if (found) {
                        const minP = Number(found.min_price);
                        const maxP = Number(found.max_price);
                        setPriceBounds({ min: minP, max: maxP });
                        const curUnit = form.getFieldValue('unit_price')
                        let newUnit = curUnit
                        if (curUnit === undefined || curUnit === null || curUnit === '') {
                          newUnit = minP
                        } else {
                          const num = Number(curUnit)
                          if (Number.isNaN(num) || num < minP) newUnit = minP
                          else if (num > maxP) newUnit = maxP
                        }
                        form.setFieldsValue({ _priceBounds: { min: minP, max: maxP }, unit_price: newUnit, price_list: valStr });
                      } else {
                        setPriceBounds({});
                        form.setFieldsValue({ _priceBounds: undefined, price_list: "", unit_price: undefined });
                      }
                    }}
                    getPopupContainer={() => document.body}
                    dropdownStyle={{ zIndex: 2147483647 }}
                  >
                    {lists.map((code) => (
                      <Select.Option key={code} value={code}>{`Lista ${code}`}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={12} span={12}>
                <WarehouseFields form={form} disabled={disabledAll} catalog={warehouseCatalog} />
              </Col>

              <Col xs={24} md={12} span={12}>
                <Form.Item
                  name="unit_price"
                  label="Precio Unitario"
                  rules={[
                    () => ({
                      validator: (_, value) => {
                        const bounds = form.getFieldValue("_priceBounds") || {};
                        const min = typeof bounds.min === "number" ? bounds.min : priceBounds.min;
                        const max = typeof bounds.max === "number" ? bounds.max : priceBounds.max;
                        const hasPriceList = Boolean(form.getFieldValue("price_list") || watchedPriceList);

                        if (!hasPriceList) {
                          if (value === undefined || value === null || value === "") return Promise.resolve();
                          const vnum = Number(value);
                          if (!Number.isNaN(vnum) && vnum === 0) return Promise.resolve();
                          return Promise.reject(new Error("El precio debe estar vacío o ser 0 si no hay lista de precios"));
                        }
                        const num = Number(value);
                        if (Number.isNaN(num)) return Promise.reject(new Error("Precio inválido"));
                        if (typeof min === "number" && num < min) return Promise.reject(new Error(`El precio debe ser >= ${min}`));
                        if (typeof max === "number" && num > max) return Promise.reject(new Error(`El precio debe ser <= ${max}`));
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <InputNumber
                    value={watchedUnitPrice ?? undefined}
                    onChange={(v) => form.setFieldsValue({ unit_price: v })}
                    disabled={!priceOk || disabledAll}
                    min={typeof priceBounds.min === "number" ? priceBounds.min : undefined}
                    max={typeof priceBounds.max === "number" ? priceBounds.max : undefined}
                    step={0.01}
                    style={{ width: "100%" }}
                    stringMode
                    placeholder="Ingresa precio unitario"
                  />
                  <div style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    {typeof priceBounds.min === "number" ? (
                      <Tag color="success" style={{ cursor: "pointer" }} onClick={() => form.setFieldsValue({ unit_price: priceBounds.min })}>
                        Min: ${Number(priceBounds.min).toLocaleString()}
                      </Tag>
                    ) : (
                      <Tag color="default">Min: —</Tag>
                    )}
                    {typeof priceBounds.max === "number" ? (
                      <Tag color="warning">Max: ${Number(priceBounds.max).toLocaleString()}</Tag>
                    ) : (
                      <Tag color="default">Max: —</Tag>
                    )}
                  </div>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item name="notes" label="Notas Adicionales">
                  <Input.TextArea
                    placeholder="Agregue observaciones o detalles específicos (máx. 140 caracteres)"
                    maxLength={140}
                    value={form.getFieldValue("notes") ?? ""}
                    onChange={(e) => form.setFieldsValue({ notes: e.target.value })}
                    autoSize={{ minRows: 1, maxRows: 3 }}
                    disabled={disabledAll}
                  />
                </Form.Item>
              </Col>
            </Row>

            <div style={{ marginTop: 10, display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={onAdd} 
                loading={loadingAdd} 
                style={{ 
                  flex: '0 1 320px', 
                  borderRadius: 8, 
                  height: 40,
                  fontWeight: 600,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.18)'
                }} 
                disabled={!priceOk || disabledAll}
              >
                Agregar Artículo
              </Button>
              {isMobile && (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <Button
                    data-oa-mobile-cart-button
                    onClick={() => setCartOpen(true)}
                    style={{ minWidth: 44 }}
                    disabled={(items || []).length === 0 || !!disabledAll}
                    aria-label={items && items.length ? `Abrir carrito, ${items.length} ítems` : 'Abrir carrito'}
                  >
                    <ShoppingCartOutlined />
                  </Button>
                  {(items && items.length > 0) ? (() => {
                    const count = (items || []).length;
                    return (
                      <div
                        aria-hidden
                        style={{
                          position: 'absolute',
                          top: -6,
                          right: -6,
                          background: '#ffec3d',
                          color: '#0b4f84',
                          padding: '2px 6px',
                          borderRadius: 12,
                          fontSize: 11,
                          fontWeight: 700,
                          boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                          whiteSpace: 'nowrap',
                          minWidth: 20,
                          textAlign: 'center'
                        }}
                      >
                        {count}
                      </div>
                    )
                  })() : null}
                </div>
              )}
            </div>
          </Form>
        </Card>
        </div>
        {!isMobile && (
          <div className="oa-items-stack-cart">
            <Collapse bordered={false} className="oa-collapse-ghost" defaultActiveKey={["cart"]}>
              <Collapse.Panel
                key="cart"
                header="Carrito"
              >
                <Card size="small" styles={{ body: { padding: 8 } }} style={{ borderRadius: 8 }}>
                  <Table
                    tableLayout="fixed"
                    columns={isMobile 
                      ? [
                          { title: 'Producto', dataIndex: 'reference', key: 'reference', ellipsis: true },
                          { title: 'Ext', dataIndex: 'extension', key: 'extension', width: 50 },
                          { title: 'Cant', dataIndex: 'quantity', key: 'quantity', width: 60, align: 'right' as const },
                          { 
                            title: 'Total', 
                            dataIndex: 'total', 
                            key: 'total', 
                            width: 80, 
                            align: 'right' as const,
                            render: (v: number) => `$${v?.toLocaleString() || 0}`
                          },
                        ]
                      : columns
                    }
                    dataSource={items}
                    pagination={{ pageSize: 5 }}
                    size="small"
                    locale={{ emptyText: 'Tu carrito está vacío - agrega artículos para continuar' }}
                    rowKey="key"
                    scroll={{ x: isMobile ? 560 : 1120 }}
                    rowClassName={(record) => {
                      const isHighlighted = highlightedRows?.has(record.key);
                      return isHighlighted ? 'highlighted-row' : '';
                    }}
                    style={isMobile ? { fontSize: '12px' } : undefined}
                    footer={() => <div style={{ textAlign: "right", fontWeight: 600 }}>Total: ${total === null || total === undefined ? "—" : total.toLocaleString()}</div>}
                  />
                </Card>
              </Collapse.Panel>
            </Collapse>
          </div>
        )}

        {isMobile && (
          <Drawer
            title={<span><ShoppingCartOutlined style={{ color: 'var(--oa-accent)', marginRight: 8 }} />Carrito</span>}
            placement="bottom"
            height="65%"
            onClose={() => setCartOpen(false)}
            open={cartOpen}
            getContainer={() => document.body}
            style={{ padding: 0, height: '100%', overflow: 'auto', zIndex: 9999 }}
            styles={{
              body: { padding: 12 },
              mask: { background: 'rgba(0,0,0,0.35)' },
            }}
          >
            <div>
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontWeight: 600 }}>Total: ${total === null || total === undefined ? "—" : total.toLocaleString()}</div>
              </div>
              {items && items.length ? (
                <div>
                  {items.map((it) => (
                    <Card key={it.key} size="small" style={{ marginBottom: 14, borderRadius: 12, padding: 0, overflow: 'hidden', boxShadow: '0 8px 20px rgba(15,32,64,0.06)', border: '1px solid rgba(15,32,64,0.08)', background: 'linear-gradient(180deg, #ffffff, #fbfdff)' }} styles={{ body: { padding: 0 } }}>
                      <div style={{ display: 'flex', alignItems: 'stretch' }}>
                        <div style={{ width: 6, background: 'var(--oa-accent)' }} />
                        <div style={{ flex: 1, padding: 14 }}>
                          <Row gutter={[10, 10]} align="middle">
                            <Col span={16}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                                <div>
                                  <Text strong style={{ fontSize: 15, color: '#072146' }}>{it.reference}</Text>
                                  <div style={{ color: '#6b7280', fontSize: 12, marginTop: 4 }}>{it.extension || '—'}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontWeight: 800, color: 'var(--oa-accent)', fontSize: 15 }}>
                                    ${typeof it.total === 'number' ? it.total.toLocaleString() : '—'}
                                  </div>
                                  <div style={{ fontSize: 12, color: '#8b98a6' }}>{it.quantity} × {it.measure_unit}</div>
                                </div>
                              </div>
                            </Col>
                            <Col span={8}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                                <div style={{ fontSize: 12, color: '#475569', textAlign: 'right' }}>
                                  {it.warehouse || '—'}
                                  {it.location ? (
                                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{it.location}</div>
                                  ) : null}
                                </div>
                                <div style={{ background: 'var(--oa-accent-bg)', color: 'var(--oa-accent)', padding: '4px 8px', borderRadius: 8, fontSize: 12, fontWeight: 600, boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.06)' }}>
                                  {it.price_list || 'Lista'}
                                </div>
                                <Space size={4}>
                                  {onEdit ? (
                                    <Button size="small" type="primary" ghost onClick={() => onEdit(it)}>
                                      Editar
                                    </Button>
                                  ) : null}
                                  {onRemove ? <Button danger size="small" onClick={() => onRemove(String(it.key))}>Eliminar</Button> : null}
                                </Space>
                              </div>
                            </Col>
                            {it.notes ? (
                              <Col span={24}><div style={{ marginTop: 8, color: '#6b7280', fontSize: 13 }}>{it.notes}</div></Col>
                            ) : null}
                          </Row>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 32, color: '#8c8c8c' }}>
                  <ShoppingCartOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                  <div style={{ fontSize: 16, marginBottom: 8 }}>Tu carrito está vacío</div>
                  <div style={{ fontSize: 14 }}>Agrega artículos para continuar</div>
                </div>
              )}
              <div style={{ marginTop: 12, textAlign: 'right' }}>
                <Button onClick={() => setCartOpen(false)} style={{ marginRight: 8 }}>Cerrar</Button>
              </div>
            </div>
          </Drawer>
        )}
      </div>
    );
  }

  
  return (
    <Row gutter={10}>
      <Col xs={24} md={9}>
        <Card 
          title={
            <Space>
              <PlusOutlined style={{ color: 'var(--oa-accent)' }} />
              <span style={{ fontWeight: 600 }}>Nuevo Artículo</span>
            </Space>
          } 
          size="small" 
          styles={{ body: { paddingTop: 12 } }} 
          style={cardStyle}
        >
          <Form
            form={form}
            layout="vertical"
            className="oa-compact"
            requiredMark={false}
            validateTrigger={["onChange", "onBlur"]}
            initialValues={{}}
            disabled={disabledAll}
          >
            <Row gutter={12} align="middle">
              <Col xs={24} md={24}>
                <Form.Item name="reference" noStyle>
                  <Input type="hidden" value={form.getFieldValue("reference") ?? ""} readOnly />
                </Form.Item>
              </Col>

              <Col xs={24} md={24}>
                <ExtensionMultiSelect form={form} />
              </Col>

              <Col xs={12} md={8}>
                <Form.Item name="measure_unit" label="Unidad de Medida" rules={[{ required: true, message: "Seleccione unidad de medida" }]}>
                  <AutoComplete
                    options={(measureUnits?.length ? measureUnits : DEFAULT_MEASURE_UNITS).map((m) => ({ value: m.value, label: m.label }))}
                    value={watchedMeasureUnit ?? undefined}
                    onChange={(v) => form.setFieldsValue({ measure_unit: v })}
                    placeholder="Ej: Unidad, Kg, Metro"
                    disabled={disabledAll}
                    style={{ width: "100%" }}
                    filterOption
                  />
                </Form.Item>
              </Col>

              <Col xs={12} md={8}>
                <Form.Item name="price_list" label="Lista de Precios" rules={[{ required: true, message: "Seleccione lista de precios" }]}>
                  <Select
                    value={form.getFieldValue("price_list") || undefined}
                    showSearch
                    placeholder={lists.length ? "Selecciona lista" : "Sin listas disponibles"}
                    disabled={(lists ?? []).length === 0 || disabledAll}
                    onChange={(val) => {
                      const valStr = val as string;
                      form.setFieldsValue({ price_list: valStr });
                      const fresh: any[] = form.getFieldValue("availablePrices") || availablePrices || [];
                      const found = (fresh || []).find((p: any) => String(p.price_list) === String(valStr));
                      if (found) {
                        const minP = Number(found.min_price);
                        const maxP = Number(found.max_price);
                        setPriceBounds({ min: minP, max: maxP });
                        const curUnit = form.getFieldValue('unit_price')
                        let newUnit = curUnit
                        if (curUnit === undefined || curUnit === null || curUnit === '') {
                          newUnit = minP
                        } else {
                          const num = Number(curUnit)
                          if (Number.isNaN(num) || num < minP) newUnit = minP
                          else if (num > maxP) newUnit = maxP
                        }
                        form.setFieldsValue({ _priceBounds: { min: minP, max: maxP }, unit_price: newUnit, price_list: valStr });
                      } else {
                        setPriceBounds({});
                        form.setFieldsValue({ _priceBounds: undefined, unit_price: undefined, price_list: "" });
                      }
                    }}
                    getPopupContainer={() => document.body}
                    dropdownStyle={{ zIndex: 2147483647 }}
                  >
                    {(lists ?? []).map((code) => (
                      <Select.Option key={code} value={code}>{`Lista ${code}`}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <WarehouseFields form={form} disabled={disabledAll} catalog={warehouseCatalog} />
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="unit_price"
                  label="Precio Unitario"
                  rules={[
                    { required: true, message: "Ingrese precio unitario" },
                    () => ({
                      validator: (_, value) => {
                        const bounds = form.getFieldValue("_priceBounds") || {};
                        const min = typeof bounds.min === "number" ? bounds.min : priceBounds.min;
                        const max = typeof bounds.max === "number" ? bounds.max : priceBounds.max;
                        const hasPriceList = Boolean(form.getFieldValue("price_list") || watchedPriceList);

                        if (!hasPriceList) {
                          if (value === undefined || value === null || value === "") return Promise.resolve();
                          const vnum = Number(value);
                          if (!Number.isNaN(vnum) && vnum === 0) return Promise.resolve();
                          return Promise.reject(new Error("El precio debe estar vacío o ser 0 si no hay lista de precios"));
                        }
                        const num = Number(value);
                        if (Number.isNaN(num)) return Promise.reject(new Error("Precio inválido"));
                        if (typeof min === "number" && num < min) return Promise.reject(new Error(`El precio debe ser >= ${min}`));
                        if (typeof max === "number" && num > max) return Promise.reject(new Error(`El precio debe ser <= ${max}`));
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <InputNumber
                    disabled={!priceOk || disabledAll}
                    min={typeof priceBounds.min === "number" ? priceBounds.min : undefined}
                    max={typeof priceBounds.max === "number" ? priceBounds.max : undefined}
                    step={0.01}
                    value={watchedUnitPrice ?? undefined}
                    onChange={(v) => form.setFieldsValue({ unit_price: v })}
                    style={{ width: "100%" }}
                    placeholder="Ingresa precio unitario"
                    stringMode
                  />
                  <div style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center" }}>
                    {typeof priceBounds.min === "number" ? (
                      <Tag color="success" style={{ cursor: "pointer" }} onClick={() => form.setFieldsValue({ unit_price: priceBounds.min })}>
                        Min: ${Number(priceBounds.min).toLocaleString()}
                      </Tag>
                    ) : (
                      <Tag color="default">Min: —</Tag>
                    )}
                    {typeof priceBounds.max === "number" ? (
                      <Tag color="warning">Max: ${Number(priceBounds.max).toLocaleString()}</Tag>
                    ) : (
                      <Tag color="default">Max: —</Tag>
                    )}
                  </div>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item name="notes" label="Notas Adicionales">
                  <Input.TextArea 
                    placeholder="Agregue observaciones o detalles específicos (máx. 140 caracteres)" 
                    maxLength={140} 
                    autoSize={{ minRows: 1, maxRows: 3 }} 
                    value={form.getFieldValue("notes") ?? ""} 
                    onChange={(e) => form.setFieldsValue({ notes: e.target.value })} 
                    disabled={disabledAll} 
                  />
                </Form.Item>
              </Col>
            </Row>

            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={onAdd} 
              loading={loadingAdd} 
              style={{ 
                width: "100%", 
                marginTop: 10, 
                height: 40,
                borderRadius: 8,
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(0,0,0,0.18)'
              }} 
              disabled={!priceOk || disabledAll}
            >
              Agregar Artículo
            </Button>
          </Form>
        </Card>
      </Col>

      <Col xs={24} md={15}>
        <Collapse bordered={false} className="oa-collapse-ghost">
          <Collapse.Panel
            key="cart"
            header={
              <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                <Space>
                  <ShoppingCartOutlined style={{ color: "var(--oa-accent)" }} />
                  <span>Carrito</span>
                </Space>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 600 }}>Total: ${total === null || total === undefined ? "—" : total.toLocaleString()}</div>
                </div>
              </div>
            }
          >
            <Card size="small" styles={{ body: { padding: 8 } }} style={{ borderRadius: 8 }}>
              <Table
                tableLayout="fixed"
                columns={isMobile 
                  ? [
                      { title: 'Producto', dataIndex: 'reference', key: 'reference', ellipsis: true },
                      { title: 'Ext', dataIndex: 'extension', key: 'extension', width: 50 },
                      { title: 'Cant', dataIndex: 'quantity', key: 'quantity', width: 60, align: 'right' as const },
                      { 
                        title: 'Total', 
                        dataIndex: 'total', 
                        key: 'total', 
                        width: 80, 
                        align: 'right' as const,
                        render: (v: number) => `$${v?.toLocaleString() || 0}`
                      },
                    ]
                  : columns
                }
                dataSource={items}
                pagination={{ pageSize: 5 }}
                size="small"
                locale={{ emptyText: "Sin ítems todavía" }}
                rowKey="key"
                scroll={{ x: isMobile ? 560 : 1020 }}
                rowClassName={(record) => {
                  const isHighlighted = highlightedRows?.has(record.key);
                  return isHighlighted ? 'highlighted-row' : '';
                }}
                style={isMobile ? { fontSize: '12px' } : undefined}
                footer={() => <div style={{ textAlign: "right", fontWeight: 600 }}>Total: ${total === null || total === undefined ? "—" : total.toLocaleString()}</div>}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>Tip: Usa listas y bodegas para clasificar correctamente cada entrada.</Text>
            </Card>
          </Collapse.Panel>
        </Collapse>
      </Col>
    </Row>
  );
};

export default ModernItemsSection;
